import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, Car, FileText, Plus, Building2, Warehouse, Scale, ArrowUpRight, ArrowDownRight, Minus, CalendarDays } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v || 0);

const AdminFinance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = month !== 'all' ? `?month=${month}` : '';
      const res = await axios.get(`${API_URL}/api/finance/dashboard${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getMonthOptions = () => {
    const opts = [{ value: 'all', label: 'Todos os meses' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
      opts.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#1a1c2a] border border-zinc-700 px-4 py-3 text-sm">
        <p className="text-white font-medium mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={p.dataKey || p.name || i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
        ))}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-medium text-white mb-1" data-testid="finance-title">Gestão Financeira</h1>
            <p className="text-zinc-500">Controlo de custos e lucros do stand</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-48 bg-[#12141D] border-zinc-800 text-white" data-testid="finance-month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#12141D] border-zinc-800">
                {getMonthOptions().map(o => (
                  <SelectItem key={o.value} value={o.value} className="text-white hover:bg-zinc-800">{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/admin/financas/despesas">
              <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="manage-expenses-btn">
                <Plus size={18} className="mr-2" /> Despesas
              </Button>
            </Link>
            <Link to="/admin/financas/relatorio">
              <Button className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700" data-testid="monthly-report-btn">
                <FileText size={18} className="mr-2" /> Mensal
              </Button>
            </Link>
            <Link to="/admin/financas/relatorio-anual">
              <Button className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700" data-testid="annual-report-btn">
                <CalendarDays size={18} className="mr-2" /> Anual
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20"><p className="text-zinc-400">A carregar dados financeiros...</p></div>
        ) : data && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8" data-testid="finance-kpi-grid">
              <KpiCard icon={DollarSign} label="Total Investido" value={fmt(data.total_invested)} color="text-white" sub={`${data.vehicle_count} viaturas`} />
              <KpiCard icon={Warehouse} label="Ativo em Stock" value={fmt(data.stock_asset_value)} color="text-blue-500" sub={`${data.stock_asset_count} viaturas à venda`} />
              <KpiCard icon={data.total_profit >= 0 ? TrendingUp : TrendingDown} label="Lucro Total" value={fmt(data.total_profit)} color={data.total_profit >= 0 ? 'text-emerald-500' : 'text-red-500'} sub="Receita - Custos - Despesas gerais" />
              <KpiCard icon={Building2} label="Despesas Gerais" value={fmt(data.general_expenses)} color="text-orange-500" sub="Renda, luz, internet..." />
              <KpiCard icon={AlertTriangle} label="Margem Baixa" value={data.low_margin_alerts?.length || 0} color={data.low_margin_alerts?.length > 0 ? 'text-yellow-500' : 'text-emerald-500'} sub="Viaturas com margem < 10%" />
            </div>

            {/* Balanço Patrimonial */}
            <div className="bg-[#12141D] border border-zinc-800 p-6 mb-8" data-testid="balance-sheet">
              <h3 className="text-white font-medium mb-6 flex items-center gap-2">
                <Scale size={20} className="text-blue-500" /> Balanço Patrimonial
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Ativos */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <ArrowUpRight size={16} className="text-emerald-500" />
                    <span className="text-emerald-400 font-medium text-sm uppercase tracking-wide">Ativos</span>
                  </div>
                  <BalanceRow label="Stock à venda" sublabel={`${data.stock_asset_count} viaturas`} value={data.stock_asset_value} color="text-blue-400" />
                  <BalanceRow label="Receita de vendas" sublabel={`${data.sold_count || 0} vendidas`} value={data.total_revenue} color="text-emerald-400" />
                  <div className="border-t border-zinc-700 pt-3 mt-3">
                    <BalanceRow label="Total de Ativos" value={(data.stock_asset_value || 0) + (data.total_revenue || 0)} color="text-emerald-400" bold />
                  </div>
                </div>

                {/* Passivos / Custos */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <ArrowDownRight size={16} className="text-red-500" />
                    <span className="text-red-400 font-medium text-sm uppercase tracking-wide">Custos</span>
                  </div>
                  <BalanceRow label="Compra de viaturas" sublabel={`${data.vehicle_count} viaturas`} value={data.total_purchase_cost} color="text-zinc-300" negative />
                  <BalanceRow label="Despesas com viaturas" sublabel="Mecânica, peças, etc." value={data.total_vehicle_expenses} color="text-zinc-300" negative />
                  <BalanceRow label="Despesas gerais" sublabel="Renda, luz, salários..." value={data.general_expenses} color="text-orange-400" negative />
                  <div className="border-t border-zinc-700 pt-3 mt-3">
                    <BalanceRow label="Total de Custos" value={(data.total_purchase_cost || 0) + (data.total_vehicle_expenses || 0) + (data.general_expenses || 0)} color="text-red-400" bold negative />
                  </div>
                </div>

                {/* Resultado / Balanço */}
                <div className="flex flex-col justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Scale size={16} className="text-blue-500" />
                    <span className="text-blue-400 font-medium text-sm uppercase tracking-wide">Resultado</span>
                  </div>
                  <div className={`flex-1 flex flex-col items-center justify-center p-6 border-2 border-dashed ${data.balance >= 0 ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                    <p className="text-zinc-400 text-sm mb-2">Balanço do Negócio</p>
                    <p className={`text-4xl font-bold ${data.balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="balance-value">
                      {fmt(data.balance)}
                    </p>
                    <p className="text-zinc-500 text-xs mt-3 text-center">Stock + Receita vendas - Custos totais</p>
                    {data.stock_invested_cost > 0 && (
                      <p className="text-zinc-600 text-xs mt-2">
                        Custo do stock atual: {fmt(data.stock_invested_cost)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Low Margin Alerts */}
            {data.low_margin_alerts?.length > 0 && (
              <div className="mb-8 bg-yellow-500/5 border border-yellow-500/20 p-5" data-testid="low-margin-alerts">
                <h3 className="text-yellow-400 font-medium mb-3 flex items-center gap-2">
                  <AlertTriangle size={18} /> Alertas de Margem Baixa
                </h3>
                <div className="space-y-2">
                  {data.low_margin_alerts.map(a => (
                    <Link key={a.id} to={`/admin/financas/viatura/${a.id}`} className="flex items-center justify-between py-2 px-3 hover:bg-yellow-500/5 transition-colors">
                      <span className="text-white">{a.label}</span>
                      <span className="text-yellow-400 font-medium">{a.margin}% margem ({fmt(a.profit)})</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Monthly Expenses Chart */}
              <div className="bg-[#12141D] border border-zinc-800 p-6" data-testid="monthly-chart">
                <h3 className="text-white font-medium mb-6">Despesas Mensais</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.monthly_chart} barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="month" tick={{ fill: '#71717a', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#71717a', fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="vehicle_expenses" name="Viaturas" fill="#DC2626" radius={[4,4,0,0]} />
                      <Bar dataKey="general_expenses" name="Gerais" fill="#f97316" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Profit per Vehicle Chart */}
              <div className="bg-[#12141D] border border-zinc-800 p-6" data-testid="profit-chart">
                <h3 className="text-white font-medium mb-6">Lucro por Viatura</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(data.vehicle_profits || []).slice(0, 8)} layout="vertical" barSize={20}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis type="number" tick={{ fill: '#71717a', fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="label" tick={{ fill: '#a1a1aa', fontSize: 11 }} width={120} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="profit" name="Lucro" radius={[0,4,4,0]}>
                        {(data.vehicle_profits || []).slice(0, 8).map((entry, i) => (
                          <Cell key={entry.id || `cell-${i}`} fill={entry.profit >= 0 ? '#10b981' : '#ef4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Vehicle Profit Table */}
            <div className="bg-[#12141D] border border-zinc-800 overflow-hidden mb-8" data-testid="vehicle-profit-table">
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
                <h3 className="text-white font-medium flex items-center gap-2"><Car size={18} className="text-red-500" /> Lucro por Viatura</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500">
                      <th className="text-left px-6 py-3 font-medium">Viatura</th>
                      <th className="text-right px-4 py-3 font-medium">Compra</th>
                      <th className="text-right px-4 py-3 font-medium">Despesas</th>
                      <th className="text-right px-4 py-3 font-medium">Custo Total</th>
                      <th className="text-right px-4 py-3 font-medium">Venda</th>
                      <th className="text-right px-4 py-3 font-medium">Lucro</th>
                      <th className="text-right px-4 py-3 font-medium">Margem</th>
                      <th className="text-center px-4 py-3 font-medium">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.vehicle_profits || []).map(v => (
                      <tr key={v.id} className="border-b border-zinc-800/50 hover:bg-white/[0.02] transition-colors">
                        <td className="px-6 py-3">
                          <span className="text-white font-medium">{v.label}</span>
                          <span className="text-zinc-500 ml-2 text-xs">({v.year})</span>
                          <span className={`ml-2 text-[10px] px-2 py-0.5 font-bold uppercase ${v.status === 'Vendido' ? 'bg-zinc-700 text-zinc-300' : v.status === 'Reservado' ? 'bg-red-600/20 text-red-400' : 'bg-emerald-600/20 text-emerald-400'}`}>{v.status}</span>
                        </td>
                        <td className="text-right px-4 py-3 text-zinc-300">{v.purchase_price > 0 ? fmt(v.purchase_price) : '-'}</td>
                        <td className="text-right px-4 py-3 text-orange-400">{v.expenses > 0 ? fmt(v.expenses) : '-'}</td>
                        <td className="text-right px-4 py-3 text-zinc-300">{fmt(v.total_cost)}</td>
                        <td className="text-right px-4 py-3 text-white font-medium">{fmt(v.sale_price)}</td>
                        <td className={`text-right px-4 py-3 font-semibold ${v.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(v.profit)}</td>
                        <td className={`text-right px-4 py-3 ${v.margin >= 10 ? 'text-emerald-400' : v.margin >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>{v.margin}%</td>
                        <td className="text-center px-4 py-3">
                          <Link to={`/admin/financas/viatura/${v.id}`} className="text-red-500 hover:text-red-400 transition-colors" data-testid={`vehicle-detail-${v.id}`}>
                            <FileText size={16} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* General Expenses Breakdown */}
            {Object.keys(data.general_by_type || {}).length > 0 && (
              <div className="bg-[#12141D] border border-zinc-800 p-6" data-testid="general-expenses-breakdown">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Building2 size={18} className="text-orange-500" /> Despesas Gerais por Tipo
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(data.general_by_type).map(([type, amount]) => (
                    <div key={type} className="bg-[#090A0F] border border-zinc-800 p-4">
                      <p className="text-zinc-500 text-xs uppercase mb-1">{typeLabels[type] || type}</p>
                      <p className="text-orange-400 font-semibold text-lg">{fmt(amount)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

const typeLabels = {
  renda: 'Renda', eletricidade: 'Eletricidade', internet: 'Internet',
  salarios: 'Salários', contabilidade: 'Contabilidade', seguros: 'Seguros',
  publicidade: 'Publicidade', manutencao: 'Manutenção', outros: 'Outros',
  mecanica: 'Mecânica', pecas: 'Peças', legalizacao: 'Legalização',
  transporte: 'Transporte', limpeza: 'Limpeza'
};

const KpiCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-[#12141D] border border-zinc-800 p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-zinc-500 text-sm">{label}</p>
      <Icon size={20} className={color} />
    </div>
    <p className={`text-2xl font-semibold ${color}`}>{value}</p>
    {sub && <p className="text-zinc-600 text-xs mt-1">{sub}</p>}
  </div>
);

const BalanceRow = ({ label, sublabel, value, color = 'text-zinc-300', bold, negative }) => (
  <div className="flex items-center justify-between">
    <div>
      <p className={`text-sm ${bold ? 'text-white font-semibold' : 'text-zinc-400'}`}>{label}</p>
      {sublabel && <p className="text-zinc-600 text-xs">{sublabel}</p>}
    </div>
    <p className={`${bold ? 'text-lg font-bold' : 'text-sm font-medium'} ${color}`}>
      {negative && value > 0 ? '- ' : ''}{fmt(value)}
    </p>
  </div>
);

export default AdminFinance;
